/* Sham Plan Selector - one selector for the cart drawer and /cart.
   The rules below are pure and unit tested. The element in the second half
   of this file holds no plan state: bundleStorage and the discount cookie
   are the sources of truth, and it re-reads them on every change. */

(function (global) {
  'use strict';

  var KNOWN_CODES = ['FREEBREAST-IM7D2', 'FREEMINCE-3J1P0'];
  var DEFAULT_CODE = 'FREEMINCE-3J1P0';
  var STORAGE_KEY = 'bundleOfferCode';

  // Selling plan group 78023622960 "Subscribe and Save".
  var PLAN_IDS = {
    '1': '690388500784',
    '2': '690388468016',
    '3': '690388435248',
    '4': '690388402480',
  };

  // initial-storage.liquid:73-74 derives the plan number by splitting the
  // plan NAME on spaces and taking index 1, so a rename in Loop silently
  // produces garbage there. Here we fail closed instead.
  function parsePlanName(name) {
    if (typeof name !== 'string' || !name) return null;
    var parts = name.trim().split(/\s+/);
    if (parts.length < 2) return null;
    var n = parseInt(parts[1], 10);
    if (isNaN(n) || String(n) !== parts[1]) return null;
    return PLAN_IDS[String(n)] ? n : null;
  }

  function resolveProtein(opts) {
    var plan = parseInt(opts.plan, 10);
    if (isNaN(plan) || plan === 0) return null;
    if (KNOWN_CODES.indexOf(opts.current) !== -1) return opts.current;
    if (KNOWN_CODES.indexOf(opts.last) !== -1) return opts.last;
    return DEFAULT_CODE;
  }

  // Lifted from the shipped /cart FPFL component's cookie rules: tokenise on
  // commas or whitespace and compare case-insensitively. A stale FPFL code
  // that isn't recognised here survives and stacks with a newly applied one.
  function stripKnownCodes(value) {
    if (typeof value !== 'string' || !value) return '';
    var tokens = value
      .split(/[,\s]+/)
      .map(function (c) { return c.trim(); })
      .filter(Boolean);

    var kept = tokens.filter(function (token) {
      return KNOWN_CODES.indexOf(token.toUpperCase()) === -1;
    });

    return kept.join(',');
  }

  function withCode(value, code) {
    var kept = stripKnownCodes(value);
    if (!code) return kept;
    return kept ? kept + ',' + code : code;
  }

  var PENDING_TIMEOUT_MS = 12000;

  function planChangeTimedOut(startedAt, now, limitMs) {
    if (typeof startedAt !== 'number' || isNaN(startedAt)) return false;
    return now - startedAt >= limitMs;
  }

  var api = {
    parsePlanName: parsePlanName,
    resolveProtein: resolveProtein,
    stripKnownCodes: stripKnownCodes,
    withCode: withCode,
    planChangeTimedOut: planChangeTimedOut,
    KNOWN_CODES: KNOWN_CODES,
    DEFAULT_CODE: DEFAULT_CODE,
    STORAGE_KEY: STORAGE_KEY,
    PLAN_IDS: PLAN_IDS,
    PENDING_TIMEOUT_MS: PENDING_TIMEOUT_MS,
  };

  if (typeof customElements !== 'undefined' && typeof HTMLElement !== 'undefined') {

    class ShamPlanSelector extends HTMLElement {
      connectedCallback() {
        this.onPlanChange = () => this.render();
        document.addEventListener('changeDeliveryPlan', this.onPlanChange);

        // bundle-page.js's processQueuedPlanChange() dispatches this once the
        // clear/re-add round trip actually lands, which is the signal setPlan()
        // needs to release the pending lock below - see the comment on setPlan().
        this.onPricesSettled = () => {
          if (this._timer) clearTimeout(this._timer);
          this._timer = null;
          this._pending = false;
          this._pendingAt = null;
          if (this._ownsError) this.clearError();
          this.render();
        };
        document.addEventListener('updateProductPrices', this.onPricesSettled);

        // jc-bundle-main.js reports plan-change failures by writing textContent
        // into [data-plan-change-error] and never clearing `hidden` — it predates
        // this slot being hidden by default. Watch for that and reveal it, so the
        // existing failure path keeps reaching the customer.
        const errorSlot = this.querySelector('[data-plan-change-error]');
        if (errorSlot && typeof MutationObserver !== 'undefined') {
          this._errorObserver = new MutationObserver(() => {
            if (errorSlot.textContent.trim()) errorSlot.hidden = false;
          });
          this._errorObserver.observe(errorSlot, { childList: true, characterData: true, subtree: true });
        }

        this.addEventListener('click', (e) => {
          const planSwitch = e.target.closest('[data-plan-switch]');
          if (planSwitch) {
            const wasSubscribed = this.currentPlan() !== 0;   // read BEFORE dispatch - setPlan() may update bundleStorage synchronously before it returns
            this.setPlan(wasSubscribed ? 0 : this.lastCadence());
          }
        });

        this.querySelector('[data-cadence]').addEventListener('change', (e) => {
          this.setPlan(parseInt(e.target.value, 10));
        });

        this.addEventListener('change', (e) => {
          const chip = e.target.closest('[data-code]');
          if (chip) this.setProtein(chip.value);
        });

        this.addEventListener('mousedown', this.guard.bind(this), true);
        this.addEventListener('touchstart', this.guard.bind(this), true);
        this.addEventListener('keydown', this.guard.bind(this), true);
        this.addEventListener('click', this.guard.bind(this), true);

        const reserve = document.querySelector('[data-calendar-reserve]');
        const reserveTarget = reserve && reserve.querySelector('#storePickupApp');
        if (reserveTarget && typeof ResizeObserver !== 'undefined') {
          const limit = parseFloat(getComputedStyle(reserve).getPropertyValue('--sham-calendar-reserve')) || 0;
          this._reserveObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
              const exceeded = limit > 0 && entry.contentRect.height > limit + 1;
              reserve.setAttribute('data-reserve-exceeded', String(exceeded));
            }
          });
          this._reserveObserver.observe(reserveTarget);
        }

        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener('changeDeliveryPlan', this.onPlanChange);
        document.removeEventListener('updateProductPrices', this.onPricesSettled);
        if (this._timer) clearTimeout(this._timer);
        if (this._reserveObserver) this._reserveObserver.disconnect();
        if (this._errorObserver) this._errorObserver.disconnect();
      }

      guard(event) {
        const control = event.target.closest('[aria-disabled="true"]');
        if (!control) return;
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Escape')) return;
        event.preventDefault();
        event.stopPropagation();
      }

      currentPlan() {
        const plan = parseInt((global.bundleStorage || {}).delivery_plan, 10);
        return isNaN(plan) ? 0 : plan;
      }

      lastCadence() {
        const stored = parseInt(sessionStorage.getItem('shamLastCadence'), 10);
        return PLAN_IDS[String(stored)] ? stored : 4;
      }

      readCookie() {
        const m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
        return m ? decodeURIComponent(m[1]) : '';
      }

      writeCookie(value) {
        document.cookie = 'discount_code=' + encodeURIComponent(value) + '; path=/; max-age=' + (60 * 60 * 24 * 14);
      }

      // Plan mutation is a DISPATCH. Never call changeCartDeliveryPlan.
      //
      // The clear/re-add round trip it triggers takes seconds, and
      // bundleStorage.delivery_plan doesn't settle until it drains - a click
      // landing mid-drain reads the stale value and computes the wrong next
      // state. The lock below blocks input until it catches up; it holds no
      // plan value of its own, so render() still derives everything from
      // bundleStorage.
      setPlan(plan) {
        if (isNaN(plan) || this._pending) return;
        if (plan !== 0) sessionStorage.setItem('shamLastCadence', String(plan));
        this.syncProtein(plan);
        this._pending = true;
        this._pendingAt = Date.now();
        this.clearError();
        this._timer = setTimeout(() => this.onPendingTimeout(), PENDING_TIMEOUT_MS);
        this.render();
        document.dispatchEvent(new CustomEvent('changeDeliveryPlan', { detail: { plan: plan } }));
      }

      onPendingTimeout() {
        if (!planChangeTimedOut(this._pendingAt, Date.now(), PENDING_TIMEOUT_MS)) return;
        this._pending = false;
        this._pendingAt = null;
        this.showError(this.text('error_generic'));
        this.render();
      }

      showError(message) {
        this._ownsError = true;
        const el = this.querySelector('[data-plan-change-error]');
        if (!el) return;
        el.textContent = message;
        el.hidden = false;
      }

      clearError() {
        this._ownsError = false;
        const el = this.querySelector('[data-plan-change-error]');
        if (!el) return;
        el.textContent = '';
        el.hidden = true;
      }

      setProtein(code) {
        if (KNOWN_CODES.indexOf(code) === -1) return;
        if (this.currentPlan() === 0) return;
        sessionStorage.setItem(STORAGE_KEY, code);
        this.writeCookie(withCode(this.readCookie(), code));
        this.render();
      }

      syncProtein(plan) {
        const current = sessionStorage.getItem(STORAGE_KEY);
        const next = resolveProtein({ plan: plan, current: current, last: current });
        if (next) sessionStorage.setItem(STORAGE_KEY, next);
        this.writeCookie(withCode(this.readCookie(), next));
      }

      render() {
        const plan = this.currentPlan();
        const subscribed = plan !== 0;
        const code = subscribed ? sessionStorage.getItem(STORAGE_KEY) : null;
        const pending = !!this._pending;
        const inactive = !subscribed || pending;

        const cadenceEl = this.querySelector('[data-cadence]');
        cadenceEl.value = String(subscribed ? plan : this.lastCadence());
        cadenceEl.setAttribute('aria-disabled', String(inactive));

        this.classList.toggle('is-subscribed', subscribed);
        this.setAttribute('aria-busy', String(pending));

        const planSwitchEl = this.querySelector('[data-plan-switch]');
        planSwitchEl.setAttribute('aria-checked', String(subscribed));
        planSwitchEl.setAttribute('aria-disabled', String(pending));

        this.querySelector('[data-switch-label]').textContent = this.text('switch_label');

        this.querySelectorAll('[data-code]').forEach((chip) => {
          chip.checked = chip.value === code;
          chip.setAttribute('aria-disabled', String(inactive));
        });
      }

      text(key) {
        if (!this._copy) {
          const node = this.querySelector('[data-copy]');
          try { this._copy = JSON.parse(node.textContent); } catch (e) { this._copy = {}; }
        }
        return this._copy[key] || '';
      }
    }

    customElements.define('sham-plan-selector', ShamPlanSelector);
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else global.ShamPlanSelectorRules = api;

})(typeof globalThis !== 'undefined' ? globalThis : this);
